import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import { Shield, AlertTriangle } from 'lucide-react';

interface AdminGuardProps {
  children: React.ReactNode;
  requiredRoles?: ('admin' | 'content_editor' | 'analyst')[];
}

export const AdminGuard: React.FC<AdminGuardProps> = ({ 
  children, 
  requiredRoles = ['admin'] 
}) => {
  const { user, loading } = useAuth();
  const [mfaVerified, setMfaVerified] = useState(false);
  const [mfaSessionExpired, setMfaSessionExpired] = useState(false);

  // Check MFA session on mount
  useEffect(() => {
    if (user) {
      const mfaSession = sessionStorage.getItem('admin_mfa_verified');
      const mfaTimestamp = sessionStorage.getItem('admin_mfa_timestamp');
      
      if (mfaSession === 'true' && mfaTimestamp) {
        const timestamp = parseInt(mfaTimestamp);
        const now = Date.now();
        const sessionDuration = 30 * 60 * 1000; // 30 minutes
        
        if (now - timestamp < sessionDuration) {
          setMfaVerified(true);
        } else {
          // MFA session expired
          sessionStorage.removeItem('admin_mfa_verified');
          sessionStorage.removeItem('admin_mfa_timestamp');
          setMfaSessionExpired(true);
        }
      }
    }
  }, [user]);

  // Handle MFA session expiry
  const handleMFAExpired = () => {
    sessionStorage.removeItem('admin_mfa_verified');
    sessionStorage.removeItem('admin_mfa_timestamp');
    setMfaSessionExpired(false);
    setMfaVerified(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner color="white" />
      </div>
    );
  }

  // If not logged in, redirect to admin auth page
  if (!user) {
    return <Navigate to="/admin/auth" replace />;
  }

  // If MFA session expired, show expiry message
  if (mfaSessionExpired) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full bg-gray-800 rounded-lg p-8 text-center">
          <div className="bg-yellow-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">
            MFA Session Expired
          </h1>
          <p className="text-gray-400 mb-6">
            Your multi-factor authentication session has expired. Please re-authenticate to continue.
          </p>
          <button
            onClick={handleMFAExpired}
            className="w-full bg-yellow-600 text-white py-2 px-4 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Re-authenticate
          </button>
        </div>
      </div>
    );
  }

  // If user doesn't have required admin role, show access denied
  if (!requiredRoles.includes(user.role as any)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full bg-gray-800 rounded-lg p-8 text-center">
          <div className="bg-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl">⚠️</span>
          </div>
          <h1 className="text-xl font-bold text-white mb-2">
            Access Denied
          </h1>
          <p className="text-gray-400 mb-6">
            You don't have permission to access the admin dashboard.
          </p>
          <div className="space-y-3">
            <Navigate to="/" replace />
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
            >
              Return to Main App
            </button>
            <button
              onClick={() => window.location.href = '/admin/auth'}
              className="w-full bg-gray-700 text-gray-300 py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Try Different Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};